import PartNumberSearchCell from "@dana/Components/DanaOrderUpload/DanaOrderUploadPartNumberSearchCell";
import {
    BlockWarning,
    ErrMsg,
    QtyError,
    QtyHint,
    QtyInput,
    ReplacementMsg,
    RtbMsg,
    StatusBadge,
    ValidateBanner,
} from "@dana/Components/DanaOrderUpload/DanaOrderUploadStyledComponents";
import { checkDateError, danaUploadErrorMessages, EditableRow } from "@dana/Components/DanaOrderUpload/DanaOrderUploadTypes";
import DatePicker from "@dana/MobiusOverrides/DatePicker/DatePicker";
import InformationIcon from "@dana/Icons/InformationIcon";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontFamilies, fontSizes, fontWeights } from "@dana/MobiusOverrides/styles/typography";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import DataTable from "@insite/mobius/DataTable";
import Icon from "@insite/mobius/Icon";
import Trash2 from "@insite/mobius/Icons/Trash2";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Clickable, DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";


const DeleteAllInvalidBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    line-height: 1.5;
    min-height: 42px;
    font: ${fontWeights.medium} ${fontSizes.medium}/1.5 ${fontFamilies.inter};
    color: ${themeColors.warningBannerText};
    background: ${themeBackgrounds.white};
    border: 1px solid ${themeColors.warningBannerBorder};
    cursor: pointer;
    margin-left: auto;
    white-space: nowrap;
`;

const mapStateToProps = (state: ApplicationStateDana) => ({
    danaGeneralSettings: getDanaGeneralSettings(state),
    uploadErrorMessages: danaUploadErrorMessages

});

interface Props {
    rows: EditableRow[];
    validCount: number;
    problemCount: number;
    showInvalidWarning: boolean;
    showRtbWarning: boolean;
    isEmea: boolean;
    onUpdateField: (id: string, field: keyof EditableRow, value: unknown) => void;
    onMarkInvalid: (id: string, errorMessage?: string, replacementPart?: ProductDto) => void;
    onDeleteRow: (id: string) => void;
    onReplaceProduct: (rowId: string, product: ProductDto, replacementPart?: ProductDto) => void;
}

type ReduxProps = ReturnType<typeof mapStateToProps>;
type ComponentProps = Props & ReduxProps;

export interface DanaOrderUploadStep2ValidateStyle {
    notesSection?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
}

export const danaOrderUploadStep2ValidateStyle: DanaOrderUploadStep2ValidateStyle = {
    notesSection: {

        lineHeight: 1.5,
        color: themeBackgrounds.darkGray,
        weight: fontWeights.bold,
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 6px;
            margin-bottom: 16px;
            background-color: #fff3cd;
            padding: 8px 12px;
            border-radius: 4px;
            fontSize: 18;
        `
    },
    tableWrapper: {
        css: css`
            position: relative;
            th, td {
                padding: 10px 12px;
                vertical-align: middle;
            }
            th {
                white-space: nowrap;
            }

             ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        overflow-x: auto;
                    `
                    ],
                    "max",
                )}
        `
    }
};

const styles = danaOrderUploadStep2ValidateStyle;

const DanaOrderUploadStep2Validate: FC<ComponentProps> = ({
    rows,
    validCount,
    problemCount,
    showInvalidWarning,
    showRtbWarning,
    isEmea,
    onUpdateField,
    onMarkInvalid,
    onDeleteRow,
    onReplaceProduct,
}) => {
    const parseDateString = (dateStr: string | undefined): Date | undefined => {
        if (!dateStr || checkDateError(dateStr, isEmea)) return undefined;
        const parts = dateStr.split("/");
        if (parts.length !== 3) return undefined;
        const [a, b, y] = parts;
        const month = isEmea ? Number(b) : Number(a);
        const day = isEmea ? Number(a) : Number(b);
        const year = Number(y);
        if (!month || !day || !year || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
        const d = new Date(year, month - 1, day);
        return Number.isNaN(d.getTime()) ? undefined : d;
    };

    const formatDateToString = (date: Date): string => {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const y = String(date.getFullYear());
        return isEmea ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
    };

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const invalidRows = rows.filter(r => !r.deleted && r.status === "invalid");

    const handleDeleteAllInvalid = () => {
        invalidRows.forEach(r => onDeleteRow(r.id));
    };

    return (
        <StyledWrapper>
            <Typography {...styles.notesSection}>
                <Icon src={InformationIcon} size={18} color={themeBackgrounds.darkGray} />
                {validCount}{" "}
                {siteMessage("Dana_WishList_PartUpload_ValidPartsInfo")}
            </Typography>

            {problemCount > 0 ? (
                <ValidateBanner variant="warn">
                    <StyledWrapper>
                        <Typography style={{ fontSize: 18, lineHeight: 1 }}>⚠
                        </Typography>
                        <Typography>
                            <Typography as="strong">{siteMessage("Dana_WishList_PartUpload_SomePartsNeedFix")}</Typography>{" "}
                            {siteMessage("Dana_WishList_PartUpload_SearchInstruction")}{" "}
                            <Typography as="strong">{translate("Add Parts to Mylist")}</Typography>.
                        </Typography>
                    </StyledWrapper>
                    {invalidRows.length > 0 && (
                        <DeleteAllInvalidBtn
                            onClick={handleDeleteAllInvalid}
                            data-test-selector="danaOrderUpload_deleteAllInvalid"
                        >
                            <Icon src={Trash2} size={16} color={themeColors.warningBannerText} />  {translate("Delete all invalid parts")} ({invalidRows.length})
                        </DeleteAllInvalidBtn>
                    )}
                </ValidateBanner>
            ) : validCount ? (
                <ValidateBanner variant="ok">
                    <StyledWrapper>
                        <Typography style={{ fontSize: 18, lineHeight: 1 }}>✓</Typography>
                        <Typography>
                            <Typography as="strong">{siteMessage("Dana_WishList_PartUpload_AllPartsValid")}</Typography>{" "}
                            {siteMessage("Dana_WishList_PartUpload_ClickAddToCart")}
                        </Typography>
                    </StyledWrapper>
                </ValidateBanner>
            ) : (
                <></>
            )}

            <StyledWrapper {...styles.tableWrapper} style={{ position: "relative" }}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader style={{ width: 36, textAlign: "center" }}>#</DataTableHeader>
                        <DataTableHeader style={{ width: 320 }}>{translate("Part No.")}</DataTableHeader>
                        <DataTableHeader style={{ width: 90, textAlign: "center" }}>{translate("Qty")}</DataTableHeader>
                        <DataTableHeader style={{ width: 190 }}>{translate("Requested Ship Date")}</DataTableHeader>
                        <DataTableHeader style={{ width: 130, textAlign: "center" }}>{translate("Status")}</DataTableHeader>
                        <DataTableHeader style={{ width: 44 }}></DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {rows
                            .filter(row => !row.deleted && row.needsReview)
                            .map((row, index) => (
                                <DataTableRow
                                    key={row.id}
                                    style={{
                                        background:
                                            row.status === "invalid"
                                                ? "#fff8f7"
                                                : row.status === "rtb" || row.hasQtyError || row.hasDateError
                                                    ? "#fffbf5"
                                                    : themeBackgrounds.white,
                                    }}
                                >
                                    <DataTableCell style={{ textAlign: "center" }}>{index + 1}</DataTableCell>
                                    <DataTableCell className="partNumber">
                                        <PartNumberSearchCell
                                            row={row}
                                            onSelectProduct={(id, result, validatedProduct, replacementPart) => {
                                                onReplaceProduct(id, validatedProduct, replacementPart);
                                            }}
                                            onSelectInvalid={(id, errorMessage, replacementPart) => {
                                                onMarkInvalid(id, errorMessage, replacementPart);
                                            }}
                                        />
                                        {row.status === "invalid" && row.errorMessage && !(row?.replacementPartNumber || row?.replacementPart) && (
                                            <ErrMsg>{row.errorMessage}</ErrMsg>
                                        )}
                                        {row.status === "invalid" && row.replacementPartNumber && (
                                            <ErrMsg>{siteMessage("Dana_WishList_PartUpload_ReplacementProduct")}</ErrMsg>
                                        )}
                                    </DataTableCell>
                                    <DataTableCell style={{ textAlign: "center" }}>
                                        {(() => {
                                            const minQty = row.minimumOrderQty && row.minimumOrderQty > 0 ? row.minimumOrderQty : 1;
                                            const packQty = row.multipleSaleQty && row.multipleSaleQty > 1 ? row.multipleSaleQty : 1;
                                            const belowMin = row.qty < minQty;
                                            const notMultiple = packQty > 1 && row.qty % packQty !== 0;

                                            const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                                const raw = parseInt(e.target.value, 10);
                                                const val = Number.isNaN(raw) ? minQty : Math.max(raw, 1);
                                                onUpdateField(row.id, "qty", val);
                                            };

                                            const handleQtyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                                                const raw = parseInt(e.target.value, 10);
                                                const val = Number.isNaN(raw) || raw < 1 ? 1 : raw;
                                                onUpdateField(row.id, "qty", val);
                                            };

                                            return (
                                                <>
                                                    <QtyInput
                                                        type="number"
                                                        min={minQty}
                                                        step={packQty}
                                                        value={row.qty}
                                                        hasError={row.hasQtyError}
                                                        onChange={handleQtyChange}
                                                        onBlur={handleQtyBlur}
                                                        data-test-selector={`danaOrderUpload_qty_${row.rowNumber}`}
                                                    />
                                                    {belowMin && (
                                                        <QtyError>
                                                            {translate(`Please update qty — minimum is ${minQty}`)}
                                                        </QtyError>
                                                    )}
                                                    {!belowMin && notMultiple && (
                                                        <QtyError>
                                                            {translate(`Please update qty — must be a multiple of ${packQty}`)}
                                                        </QtyError>
                                                    )}
                                                    {!row.hasQtyError && (minQty > 1 || packQty > 1) && (
                                                        <QtyHint>
                                                            {minQty > 1 && packQty > 1
                                                                ? translate(`Min ${minQty}, sold in ${packQty}s`)
                                                                : minQty > 1
                                                                    ? translate(`Min qty: ${minQty}`)
                                                                    : translate(`Sold in ${packQty}s`)}
                                                        </QtyHint>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </DataTableCell>
                                    <DataTableCell style={{ width: 190, minWidth: 190 }}>
                                        <StyledWrapper style={{ width: 190 }}>
                                            <DatePicker
                                                selectedDay={parseDateString(row.requestedShipDate)}
                                                onDayChange={({ selectedDay }) => {
                                                    onUpdateField(
                                                        row.id,
                                                        "requestedShipDate",
                                                        selectedDay ? formatDateToString(selectedDay) : "",
                                                    );
                                                }}
                                                dateTimePickerProps={{
                                                    minDate: todayMidnight,
                                                    format: isEmea ? "dd/MM/yyyy" : "MM/dd/yyyy",
                                                }}
                                                cssOverrides={{
                                                    datePicker: css`
                                                        .react-datetime-picker {
                                                            width: 100%;
                                                        }
                                                        .react-datetime-picker__clear-button {
                                                            padding-right: 4px;
                                                        }
                                                    `,
                                                }}
                                                data-test-selector={`danaOrderUpload_datepicker_${row.rowNumber}`}
                                            />
                                            {checkDateError(row.requestedShipDate, isEmea) && (
                                                <QtyError>
                                                    {row.requestedShipDate
                                                        ? translate(`Invalid or past date, format should be ${isEmea ? "DD/MM/YYYY" : "MM/DD/YYYY"}`)
                                                        : translate(`Required, format should be ${isEmea ? "DD/MM/YYYY" : "MM/DD/YYYY"}`)}
                                                </QtyError>
                                            )}
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell style={{ width: 130, textAlign: "center", whiteSpace: "nowrap" }}>
                                        <StatusBadge variant={row.status}>
                                            {row.status === "valid"
                                                ? "✓ Valid"
                                                : row.status === "rtb"
                                                    ? "⚠ Right to Buy"
                                                    : "✗ Invalid"}
                                        </StatusBadge>
                                    </DataTableCell>
                                    <DataTableCell style={{ width: 44, textAlign: "center" }}>
                                        <Clickable
                                            onClick={() => onDeleteRow(row.id)}
                                            title={translate("Remove")}
                                            data-test-selector={`danaOrderUpload_delete_${row.rowNumber}`}
                                        >
                                            <Icon src={Trash2} size={20} color={themeColors.primary.richBlack} />
                                        </Clickable>
                                    </DataTableCell>

                                </DataTableRow>
                            ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {invalidRows.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <DeleteAllInvalidBtn
                        onClick={handleDeleteAllInvalid}
                        data-test-selector="danaOrderUpload_deleteAllInvalid_footer"
                    >
                        <Icon src={Trash2} size={16} color={themeColors.warningBannerText} />  {translate("Delete all invalid parts")} ({invalidRows.length})
                    </DeleteAllInvalidBtn>
                </div>
            )}
            {problemCount === 0 && validCount === 0 && (
                <BlockWarning variant="invalid">
                    <Typography>⚠</Typography>
                    {siteMessage("Dana_WishList_NoProductsFoundForAddToList")}
                </BlockWarning>
            )}
            {showInvalidWarning && problemCount > 0 && (
                <BlockWarning variant="invalid">
                    <Typography>⚠</Typography>
                    {siteMessage("Dana_WishList_PartUpload_CorrectOrDeleteInvalid")}
                </BlockWarning>
            )}
            {showRtbWarning && problemCount > 0 && (
                <BlockWarning variant="rtb">
                    <Typography>⚠</Typography> {siteMessage("Dana_WishList_PartUpload_DeleteNoRightsToBuy")}
                </BlockWarning>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(DanaOrderUploadStep2Validate);
